import * as React from 'react';
import * as CSSTransition from 'react-transition-group/CSSTransition';
import * as TransitionGroup from 'react-transition-group/TransitionGroup';

import {
  FocusZone,
  FocusZoneDirection,
  FocusZoneTabbableElements,
  getRTL,
  IKeytipProps,
  ITheme,
  KeyCodes,
  memoizeFunction,
  VerticalDivider,
} from '@fluentui/react';

import { doNotAllowFocusEventToPropagate, MenuOpenOnExecuteParameter } from '@1js/acui-common';
import { AppContext, AppContextType } from '@1js/acui-context';
import { isShyRibbonEnabled, ribbonExperiments } from '@1js/acui-experiments';
import { deriveTabKeytip, getTopLevelKeytipProps } from '@1js/acui-keytip';
import { MenuDefinition, MenuSection } from '@1js/acui-menu';
import { AppOverflowPaddleControl } from '@1js/acui-overflow-paddle-control';
import { AppResizeGroup, MeasuredContext } from '@1js/acui-resize-group';
import { AppThemeConsumer } from '@1js/acui-theme';
import { getRectSpanningOffset, isReducedAnimations } from '@1js/window-util';

import { RibbonButton, RibbonFlyoutAnchor } from '../../Controls';
import {
  checkRibbonOffsetSpaceAvailable,
  getComponentsToMeasure,
  resetComponentMaps,
  SpanningOffsetDict,
} from '../../utilities';
import { RibbonControlGroupDefinitionSetSLR, RibbonMode } from '../Ribbon/Ribbon.Props';
import { RibbonControlGroup, RibbonControlGroupBase } from '../RibbonControlGroup';
import { addLayoutCacheKeyToRibbonState } from './addLayoutCacheKeyToRibbonState';
import {
  estimateRibbonScalingSteps,
  scaleRibbonDownWithBuildTimeScaling,
  scaleRibbonDownWithEstimatedWidth,
  scaleRibbonUpWithEstimatedWidth,
} from './QuickScaling';
import {
  bottomBarTransitionDurationMs,
  getModeChevronStyles,
  getRibbonBottomBarClassNames,
  getRibbonGroupDividerClassNames,
  modeSwitchTransitionClassNames,
  tabSwitchTransitionClassNames,
} from './RibbonBottomBar.classNames';
import { RibbonBottomBarProps, RibbonModeToggleProps } from './RibbonBottomBar.Props';
import {
  addScalingStepsToCache,
  batchScaleRibbon,
  getCachedInitialScalingStepsToApply,
  getDefaultScalingSteps,
  getInitialScalingState,
  scaleRibbonDown,
  scaleRibbonUp,
} from './ribbonBottomBarScaling';
import {
  EstimatedScalingWidthDict,
  GetScaleDownFunctionOptions,
  RibbonScalingState,
  RibbonScalingStateSLR,
  ScaledRibbonGroupBase,
  ScaledRibbonGroupMLR,
  ScaledRibbonGroupSLR,
} from './ribbonBottomBarScalingTypes';

export const ribbonModeToggleId = 'RibbonModeToggle';

/**
 * With build-time scaling, we will first render the ribbon directly into the scaled state. At some point after boot, we are going to replace the build-time scaling logic with todays scaling code.
 * We need to do this since there is a possibility of delay-rendering additional controls that we can't measure at boot such as add-ins, server-based controls, additional tabs, etc.
 * Adding an AppResizeGroup after boot would cause a visible flicker on the screen, since it would have to hide all content for measurement. In order to prevent this, we are going to use the
 * 'measureContentVisibly' prop on the AppResizeGroup. This will allow for a seamless transition from build-time scaling to standard scaling after the boot process is complete.
 * An added benefit of this approach is that if we hit an edge case where build-time scaling is wrong and renders too much content, the AppResizeGroup will immediately notice this and
 * apply additional scaling steps until it fits.
 * The transition from measureContentVisibly to !measureContentVisibly requires us to ignore cached values and calculate the content widths as if it was the first render, so we need to use
 * the following completedResizeGroupTransition variable to make sure cached measurements are ignored until the transition is complete
 */
let completedResizeGroupTransition = false;
let estimatedScalingWidths: EstimatedScalingWidthDict = {};

export const setEstimatedScalingWidths = (estimatedWidths: EstimatedScalingWidthDict) => {
  estimatedScalingWidths = estimatedWidths;
};

const getScaleDownFunctionWithCacheKey = memoizeFunction((opts: GetScaleDownFunctionOptions) => {
  return (scalingState: RibbonScalingStateSLR, measuredDimension?: number) => {
    const { quickScalingIsEnabled, mode } = opts;
    /* All scaling steps for the given tab are estimated at once because we know that the DOM contents match the scalingState */
    if (quickScalingIsEnabled && Object.keys(estimatedScalingWidths).length === 0 && measuredDimension && mode) {
      setEstimatedScalingWidths(estimateRibbonScalingSteps(scalingState, measuredDimension, mode));
    }

    const nextState = quickScalingIsEnabled
      ? scaleRibbonDownWithEstimatedWidth(scalingState, estimatedScalingWidths)
      : scaleRibbonDown(scalingState);

    if (nextState === undefined) {
      return undefined;
    }

    return addLayoutCacheKeyToRibbonState(nextState);
  };
});

const getScaleUpFunctionWithCacheKey = memoizeFunction((quickScalingIsEnabled: boolean) => {
  return (scalingState: RibbonScalingStateSLR) => {
    const nextState = quickScalingIsEnabled
      ? scaleRibbonUpWithEstimatedWidth(scalingState, estimatedScalingWidths)
      : scaleRibbonUp(scalingState);

    if (nextState === undefined) {
      return undefined;
    }
    return addLayoutCacheKeyToRibbonState(nextState);
  };
});

const batchScaleRibbonWithCacheKey = (scalingState: RibbonScalingStateSLR, scalingStepsToApply: number) => {
  const nextState = batchScaleRibbon(scalingState, scalingStepsToApply);
  if (nextState === undefined) {
    return undefined;
  }
  return addLayoutCacheKeyToRibbonState(nextState);
};

export const ribbonBottomBarContainerId = 'outerRibbonContainer';
const innerRibbonContainerId = 'innerRibbonContainer';

export class RibbonBottomBar extends React.PureComponent<RibbonBottomBarProps, {}> {
  public static displayName = 'RibbonBottomBar';
  static contextType: React.Context<AppContextType> = AppContext;
  private focusZone = React.createRef<FocusZone>();
  private ribbonControlGroups: RibbonControlGroupBase[] = [];
  private resizeGroupHasRenderedData = false;
  private ribbonMode?: RibbonMode;
  private spanningOffsetDict: SpanningOffsetDict = {};
  private paddleContainer = React.createRef<AppOverflowPaddleControl>();
  private lastKnownDataCacheKey: string = '';

  constructor(props: RibbonBottomBarProps, context: React.ContextType<typeof AppContext>) {
    super(props, context);
    // The AppResizeGroup doesn't need to transition if it isn't rendered using build-time scaling
    completedResizeGroupTransition = !props.buildTimeControlWidths;
  }

  /**
   * When the component props update, make sure the innerRef is updated.
   */
  public componentDidUpdate(prevProps: Readonly<RibbonBottomBarProps>): void {
    if (prevProps.innerRef === this.props.innerRef) {
      return;
    }

    if (prevProps.innerRef) {
      prevProps.innerRef.current = null;
    }

    if (this.props.innerRef) {
      this.props.innerRef.current = this;
    }
  }

  /**
   * When the component has mounted, update the innerRef.
   */
  public componentDidMount(): void {
    if (this.props.innerRef) {
      this.props.innerRef.current = this;
    }
  }

  public render() {
    const {
      ariaLabeledBy,
      controlGroups,
      id,
      initialScalingSteps,
      isSpanning,
      ribbonModeToggleProps,
      scalingSteps,
      controlRendererFunction,
      controlInMenuRendererFunction,
      appStrings,
      onError,
      disableAnimations,
      onReduceData,
      buildTimeControlWidths,
      onWarning,
    } = this.props;
    const { isExperimentalFeatureEnabled } = this.context;
    const isMLREnabled = isExperimentalFeatureEnabled?.(ribbonExperiments.multilineRibbonIsEnabled);

    if (controlGroups.type === 'Multiline' && !isMLREnabled) {
      throw new Error("Can't render a multi-line ribbon while the multilineRibbonIsEnabled experiment is off");
    }
    // Each time we render the ribbon, we should clear out what control groups we have as they may change
    // and we need to make sure we have an accurate list.
    this.ribbonControlGroups = [];

    const cachedScalingIsEnabled = isExperimentalFeatureEnabled?.(ribbonExperiments.ribbonCachedScalingIsEnabled);
    const initialState = getInitialScalingState(
      id,
      controlGroups,
      scalingSteps || getDefaultScalingSteps(controlGroups),
      initialScalingSteps,
      controlRendererFunction,
      controlInMenuRendererFunction,
      cachedScalingIsEnabled ? getCachedInitialScalingStepsToApply(controlGroups.type, id, onError) : undefined
    );

    if (this.ribbonMode !== initialState.type) {
      this.ribbonMode = initialState.type;
      resetComponentMaps();
      this.spanningOffsetDict = {};
    }

    const quickScalingIsEnabled =
      !!isExperimentalFeatureEnabled?.(ribbonExperiments.quickRibbonScalingIsEnabled) &&
      /* WI#4080055 tracks estimated scaling for spanning scenarios */
      !isSpanning;

    let dataNeedsMeasuring = !!this.props.isSpanning || !completedResizeGroupTransition;
    const initialRibbonData = addLayoutCacheKeyToRibbonState(initialState);
    /* If the ribbon's cacheKey changes, then the measurements that we've stored are no longer valid */
    if (initialRibbonData.cacheKey !== this.lastKnownDataCacheKey) {
      this.lastKnownDataCacheKey = initialRibbonData.cacheKey;
      estimatedScalingWidths = {};
      dataNeedsMeasuring = dataNeedsMeasuring || quickScalingIsEnabled;
    }
    const measureContentVisibly = !!buildTimeControlWidths;
    const bottomBarResizeGroup = (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          const styles = getRibbonBottomBarClassNames(theme, {
            ribbonMode: controlGroups.type,
            isModeChevronVisible: !!(ribbonModeToggleProps && ribbonModeToggleProps.onRibbonModeToggle),
            reduceAnimations: isReducedAnimations() || disableAnimations,
          });
          return (
            <AppResizeGroup
              id={id}
              className={styles.resizeGroup}
              onRenderData={this.renderScaledGroups(theme)}
              onReduceData={
                onReduceData ??
                getScaleDownFunctionWithCacheKey({
                  quickScalingIsEnabled,
                  measureContentVisibly,
                  onError,
                  mode: initialState.type,
                })
              }
              onGrowData={getScaleUpFunctionWithCacheKey(quickScalingIsEnabled)}
              onBatchScaleData={
                isExperimentalFeatureEnabled?.(ribbonExperiments.batchScaleDataInRibbon)
                  ? batchScaleRibbonWithCacheKey
                  : undefined
              }
              data={
                measureContentVisibly
                  ? scaleRibbonDownWithBuildTimeScaling(
                      initialRibbonData,
                      buildTimeControlWidths!,
                      !!isExperimentalFeatureEnabled?.(ribbonExperiments.multilineRibbonIsEnabled),
                      onWarning,
                      controlGroups.type
                    )
                  : initialRibbonData
              }
              onFocus={this.onFocus}
              isScalingComplete={isSpanning ? this.isScalingComplete : undefined}
              dataNeedsMeasuring={dataNeedsMeasuring}
              onAfterScalingComplete={this.measureRenderedData}
              measureContentVisibly={measureContentVisibly}
            />
          );
        }}
      </AppThemeConsumer>
    );

    const bottomBarContent =
      !this.props.paddleProps?.disable && this.props.paddleProps ? (
        <AppOverflowPaddleControl
          ref={this.paddleContainer}
          downChevronIcon={this.props.paddleProps.ribbonPaddlesIcon}
          disableModeTogglePadding={this.props.paddleProps.disableModeTogglePadding ?? false}
          rightRibbonPaddleLabel={appStrings.rightRibbonPaddleLabel}
          leftRibbonPaddleLabel={appStrings.leftRibbonPaddleLabel}
          onPaddlesShown={this.props.paddleProps.onPaddlesShown}
          onPaddleExecuted={this.props.paddleProps.onPaddleExecuted}
        >
          {bottomBarResizeGroup}
        </AppOverflowPaddleControl>
      ) : (
        bottomBarResizeGroup
      );

    return (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          const styles = getRibbonBottomBarClassNames(theme, {
            ribbonMode: controlGroups.type,
            isModeChevronVisible: !!(ribbonModeToggleProps && ribbonModeToggleProps.onRibbonModeToggle),
            reduceAnimations: isReducedAnimations() || disableAnimations,
          });
          return (
            <FocusZone
              componentRef={this.focusZone}
              direction={FocusZoneDirection.horizontal}
              data-is-focusable={true}
              handleTabKey={FocusZoneTabbableElements.inputOnly}
              isCircularNavigation={true}
              allowFocusRoot={true}
              onKeyDown={this.onKeyDown}
              doNotAllowFocusEventToPropagate={doNotAllowFocusEventToPropagate}
              onActiveElementChanged={this.onActiveElementChanged}
            >
              <div className={styles.root} aria-labelledby={ariaLabeledBy} role="tabpanel">
                <div className={styles.tabContentContainer} id={ribbonBottomBarContainerId}>
                  {bottomBarContent}
                </div>
                {ribbonModeToggleProps &&
                  ribbonModeToggleProps.onRibbonModeToggle &&
                  this.renderModeChevron(initialState.type, ribbonModeToggleProps, appStrings.ribbonModeChevronLabel)}
              </div>
            </FocusZone>
          );
        }}
      </AppThemeConsumer>
    );
  }

  private onFocus = () => {
    const { onFocus } = this.props;
    if (onFocus) {
      onFocus();
    }
  };

  public focus = (): boolean => {
    if (this.focusZone.current) {
      this.focusZone.current.focus(true /*forceIntoFirstElement*/);
      return true;
    }
    return false;
  };

  private onActiveElementChanged = (element?: HTMLElement) => {
    const { paddleProps } = this.props;

    // If the element we are moving focus to is off the screen, we should scroll it into view
    // and update the paddles.
    if (!paddleProps?.disable && this.paddleContainer.current?.isPaddlesVisible()) {
      element?.scrollIntoView({ block: 'nearest' });
      this.paddleContainer.current?.handleChildOverflow();
    }
  };

  private measureRenderedData = (ribbonScalingState: RibbonScalingState) => {
    const { id, onAfterScalingCompleted, onError, paddleProps, buildTimeControlWidths } = this.props;
    this.resizeGroupHasRenderedData = true;

    if (!buildTimeControlWidths) {
      completedResizeGroupTransition = true;
    }

    const scalingStepsExhausted = ribbonScalingState.appliedScalingStepCount === ribbonScalingState.scalingSteps.length;

    // Attempt to add ribbon paddles if we have exhausted all scaling steps given for
    // the ribbon tab else hide the paddles.
    if (!paddleProps?.disable) {
      if (scalingStepsExhausted) {
        this.paddleContainer.current?.handleChildOverflow();
      } else {
        this.paddleContainer.current?.hideRibbonPaddles();
      }
    }

    if (this.context.isExperimentalFeatureEnabled?.(ribbonExperiments.ribbonCachedScalingIsEnabled)) {
      addScalingStepsToCache(ribbonScalingState.type, id, ribbonScalingState.appliedScalingStepCount, onError);
    }

    if (!onAfterScalingCompleted) {
      return;
    }

    if (ribbonScalingState.type === 'Multiline') {
      onAfterScalingCompleted({
        id: ribbonScalingState.id,
        scalingStepsApplied: ribbonScalingState.appliedScalingStepCount,
        scalingStepsExhausted,
        innerWindowWidth: window.innerWidth,
      });
      return;
    }

    const { scaledRibbonGroups, scaledEndOfRibbonOverflowMenu } = ribbonScalingState;
    const shouldRenderSingleOverflowMenu = scaledEndOfRibbonOverflowMenu.sections.length !== 0;

    let itemsInOverflows = 0;
    if (!shouldRenderSingleOverflowMenu) {
      itemsInOverflows = scaledRibbonGroups.reduce(
        (acc: number, group: ScaledRibbonGroupSLR) => acc + group.overflowControls.length,
        0
      );
    } else {
      const itemsInOwnOverflowsCount = scaledRibbonGroups.reduce(
        (acc: number, group: ScaledRibbonGroupSLR) => acc + (group.hasOwnOverflow ? group.overflowControls.length : 0),
        0
      );
      const itemsInSingleOverflowCount = scaledEndOfRibbonOverflowMenu.sections.reduce(
        (acc: number, section: MenuSection) => acc + section.controls.length,
        0
      );
      itemsInOverflows = itemsInOwnOverflowsCount + itemsInSingleOverflowCount;
    }

    onAfterScalingCompleted({
      id: ribbonScalingState.id,
      itemsInOverflows,
      scalingStepsApplied: ribbonScalingState.appliedScalingStepCount,
      scalingStepsExhausted,
      innerWindowWidth: window.innerWidth,
    });
  };

  private getKeytipPropsForRibbonOverflowMenu = (keytipContent: string): IKeytipProps => {
    let keytipProps: IKeytipProps = this.generateKeytipPropsFromContent(keytipContent);
    const [firstGroup] = this.props.controlGroups.groups;
    if (firstGroup && firstGroup.keytipProps) {
      keytipProps = this.mergeKeySequencesWithFirstGroup(firstGroup.keytipProps.keySequences, keytipProps);
    }
    return keytipProps;
  };

  private generateKeytipPropsFromContent = memoizeFunction((keytipContent: string) => {
    return {
      content: keytipContent,
      hasMenu: true,
      keySequences: [keytipContent.toLocaleLowerCase()],
    };
  });

  private mergeKeySequencesWithFirstGroup = memoizeFunction(
    (firstGroupSequences: string[], keytipProps: IKeytipProps) => {
      // Add the first item in firstGroupSequences keytipProps (which is the tab header keytip).
      // This will create the correct full sequence for the overflow button keytip
      const newProps = { ...keytipProps };
      newProps.keySequences = [...firstGroupSequences.slice(0, -1), ...keytipProps.keySequences];
      return newProps;
    }
  );

  private renderRibbonOverflowMenu = (
    scaledEndOfRibbonOverflowMenu: MenuDefinition,
    ribbonScalingState: RibbonScalingState
  ): JSX.Element => {
    if (ribbonScalingState.type === 'Multiline') {
      throw new Error('Cannot render overflow menus in MLR');
    }

    const { onOverflowMenuOpen, onOverflowMenuClose } = getRibbonOverflowMenuOpenAndCloseCommandHandler(
      ribbonScalingState.ribbonGroupDefinitions
    );

    const {
      appStrings,
      overflowMenuLauncherIcon,
      controlRendererFunction,
      controlInMenuRendererFunction,
      disableAnimations,
      deferredOverflowMenuAs,
      preventMenuExecution,
    } = this.props;

    return (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          const classNames = getRibbonBottomBarClassNames(theme, {
            ribbonMode: ribbonScalingState.type,
            reduceAnimations: isReducedAnimations() || disableAnimations,
          });
          return (
            <div className={classNames.groupContainer}>
              <RibbonControlGroup
                type={'SingleLine'}
                ref={(elem: RibbonControlGroup) => {
                  this.ribbonControlGroups.push(elem);
                }}
                multiMenuOverflowControls={scaledEndOfRibbonOverflowMenu.sections}
                overflowMenuLauncherLabel={appStrings.bottomBarOverflowMenuLabel || ''}
                id={'RibbonOverflowMenu'}
                onOverflowMenuOpen={onOverflowMenuOpen}
                onOverflowMenuClose={onOverflowMenuClose}
                controlRendererFunction={controlRendererFunction}
                controlInMenuRendererFunction={controlInMenuRendererFunction}
                overflowMenuLauncherIcon={overflowMenuLauncherIcon}
                keytipProps={
                  appStrings.overflowWellKeytip
                    ? this.getKeytipPropsForRibbonOverflowMenu(appStrings.overflowWellKeytip)
                    : undefined
                }
                groupLabel={appStrings.bottomBarOverflowMenuLabel || ''}
                deferredOverflowMenuAs={deferredOverflowMenuAs}
                preventMenuExecution={preventMenuExecution}
              />
            </div>
          );
        }}
      </AppThemeConsumer>
    );
  };

  private renderScaledGroups = (theme: ITheme) => (ribbonScalingState: RibbonScalingState): JSX.Element => {
    return this.renderScaledGroupsBase(ribbonScalingState, true /* firstGroup */, theme);
  };

  private renderScaledGroupsBase = (
    ribbonScalingState: RibbonScalingState,
    firstGroup: boolean,
    theme: ITheme
  ): JSX.Element => {
    const scaledRibbonGroups: ScaledRibbonGroupBase[] = ribbonScalingState.scaledRibbonGroups;

    if (!firstGroup && (scaledRibbonGroups.length < 1 || scaledRibbonGroups[0] === undefined)) {
      return <></>;
    }

    const { disableAnimations } = this.props;
    const reducedAnimations = disableAnimations || isReducedAnimations();

    const scaledEndOfRibbonOverflowMenu: MenuDefinition | undefined =
      ribbonScalingState.type === 'SingleLine' ? ribbonScalingState.scaledEndOfRibbonOverflowMenu : undefined;

    const shouldRenderSingleOverflowMenu: boolean =
      !!scaledEndOfRibbonOverflowMenu && scaledEndOfRibbonOverflowMenu.sections.length !== 0;

    const isMLREnabled = !!this.context.isExperimentalFeatureEnabled?.(ribbonExperiments.multilineRibbonIsEnabled);

    const animateControlGroups =
      !reducedAnimations &&
      // Don't animate controls while ResizeGroup is doing determining the initial content to render.
      this.resizeGroupHasRenderedData;
    const getDividerClassNames = () => getRibbonGroupDividerClassNames(this.props.controlGroups.type, theme);

    // if the MLR is enabled & the ribbon mode toggle is being shown, we want to draw a vertical divder after the last group
    const dualRibbonModesEnabled =
      isMLREnabled && this.props.ribbonModeToggleProps && this.props.ribbonModeToggleProps.onRibbonModeToggle;
    const numberOfGroupsToRenderDivider = dualRibbonModesEnabled
      ? scaledRibbonGroups.length
      : scaledRibbonGroups.length - 1;

    const coreControlGroupContent = (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          const classNames = getRibbonBottomBarClassNames(theme, {
            ribbonMode: this.props.controlGroups.type,
            reduceAnimations: reducedAnimations,
          });
          return (
            <MeasuredContext.Consumer>
              {({ isMeasured }) => (
                <div
                  className={classNames.bottomBarContainer}
                  data-automation-type={this.props.dataAutomationType || 'RibbonBottomBarContainer'}
                  data-unique-id={this.props.dataUniqueId || 'RibbonBottomBarContainer'}
                  id={innerRibbonContainerId}
                >
                  {scaledRibbonGroups.map((group, index) => (
                    <div key={group.id} className={classNames.groupContainer}>
                      {group.type === 'SingleLine'
                        ? this.renderControlGroupSLR(
                            group as ScaledRibbonGroupSLR,
                            shouldRenderSingleOverflowMenu,
                            !isMeasured && animateControlGroups,
                            ribbonScalingState.id
                          )
                        : this.renderControlGroupMLR(
                            group as ScaledRibbonGroupMLR,
                            !isMeasured && animateControlGroups,
                            ribbonScalingState.id
                          )}
                      {(index < numberOfGroupsToRenderDivider || shouldRenderSingleOverflowMenu) && (
                        <VerticalDivider getClassNames={getDividerClassNames} />
                      )}
                    </div>
                  ))}
                  {shouldRenderSingleOverflowMenu && (
                    <div>
                      {this.renderRibbonOverflowMenu(scaledEndOfRibbonOverflowMenu!, ribbonScalingState)}
                      {dualRibbonModesEnabled && <VerticalDivider getClassNames={getDividerClassNames} />}
                    </div>
                  )}
                </div>
              )}
            </MeasuredContext.Consumer>
          );
        }}
      </AppThemeConsumer>
    );

    return !reducedAnimations ? (
      <MeasuredContext.Consumer>
        {({ isMeasured }) => (
          <TransitionGroup>
            <CSSTransition
              enter={!reducedAnimations && !isMeasured}
              exit={!reducedAnimations && !isMeasured}
              key={ribbonScalingState.id}
              timeout={bottomBarTransitionDurationMs}
              classNames={tabSwitchTransitionClassNames}
            >
              <TransitionGroup component={null}>
                <CSSTransition
                  enter={!reducedAnimations && !isMeasured}
                  exit={!reducedAnimations && !isMeasured}
                  key={`${ribbonScalingState.type}`}
                  timeout={bottomBarTransitionDurationMs}
                  classNames={modeSwitchTransitionClassNames}
                >
                  {coreControlGroupContent}
                </CSSTransition>
              </TransitionGroup>
            </CSSTransition>
          </TransitionGroup>
        )}
      </MeasuredContext.Consumer>
    ) : (
      coreControlGroupContent
    );
  };

  private renderControlGroup = (props: any) => {
    const ControlGroupRenderer = this.props.ribbonControlGroupRenderer || RibbonControlGroup;
    return <ControlGroupRenderer {...props} />;
  };

  private renderControlGroupSLR = (
    group: ScaledRibbonGroupSLR,
    shouldRenderSingleOverflowMenu: boolean,
    animate: boolean,
    tabId: string
  ): JSX.Element => {
    return this.renderControlGroup({
      type: group.type,
      componentRef: (elem: RibbonControlGroup) => {
        // When using a store connected control group, we need to get the inner
        // wrapped instance to be able to get at its functions
        // tslint:disable-next-line:no-any
        this.ribbonControlGroups.push((elem as any)?.getWrappedInstance ? (elem as any).getWrappedInstance() : elem);
      },
      animate,
      topLevelControls: group.topLevelControls,
      overflowControls: group.hasOwnOverflow || !shouldRenderSingleOverflowMenu ? group.overflowControls : undefined,
      overflowMenuLauncherDisabled: group.overflowMenuLauncherDisabled,
      overflowMenuLauncherLabel: group.overflowMenuLauncherLabel,
      onOverflowMenuOpen: group.onOverflowMenuOpen,
      onOverflowMenuClose: group.onOverflowMenuClose,
      id: group.id,
      controlRendererFunction: this.props.controlRendererFunction,
      controlInMenuRendererFunction: this.props.controlInMenuRendererFunction,
      overflowMenuLauncherIcon: this.props.overflowMenuLauncherIcon,
      keytipProps: group.keytipProps,
      groupLabel: group.groupLabel,
      isRibbonSpanning: this.props.isSpanning && this.ribbonMode === 'SingleLine',
      deferredOverflowMenuAs: this.props.deferredOverflowMenuAs,
      spanningComponentId: this.spanningOffsetDict[tabId] ? this.spanningOffsetDict[tabId].componentId : undefined,
      spanningOffset: this.spanningOffsetDict[tabId] ? this.spanningOffsetDict[tabId].spanningOffset : 0,
      preventMenuExecution: this.props.preventMenuExecution,
      fastDisabled: group.fastDisabled,
    });
  };

  private renderControlGroupMLR = (group: ScaledRibbonGroupMLR, animate: boolean, tabId: string): JSX.Element => {
    return this.renderControlGroup({
      type: group.type,
      animate: animate && !group.disableAnimations,
      componentRef: (elem: RibbonControlGroup) => {
        // When using a store connected control group, we need to get the inner
        // wrapped instance to be able to get at its functions
        // tslint:disable-next-line:no-any
        this.ribbonControlGroups.push((elem as any)?.getWrappedInstance ? (elem as any).getWrappedInstance() : elem);
      },
      controls: group.controls,
      id: group.id,
      controlRendererFunction: this.props.controlRendererFunction,
      controlInMenuRendererFunction: this.props.controlInMenuRendererFunction,
      groupLabel: group.groupLabel,
      layout: group.layout,
      groupLauncher: group.groupLauncher,
      hideGroupLabel: group.hideGroupLabel,
      isRibbonSpanning: this.props.isSpanning && this.ribbonMode === 'Multiline',
      spanningComponentId: this.spanningOffsetDict[tabId] ? this.spanningOffsetDict[tabId].componentId : undefined,
      spanningOffset: this.spanningOffsetDict[tabId] ? this.spanningOffsetDict[tabId].spanningOffset : 0,
      fastDisabled: group.fastDisabled,
    });
  };

  private onModeChevronFocus = () => {
    if (this.paddleContainer.current?.isEndPaddleVisible()) {
      const ribbonContainer = document.querySelector(`#${this.props.id}`);
      if (ribbonContainer) {
        ribbonContainer.scrollLeft = ribbonContainer.scrollWidth;
        this.paddleContainer.current?.handleChildOverflow();
      }
    }
  };

  public renderModeChevron = (
    mode: RibbonMode,
    ribbonModeToggleProps: RibbonModeToggleProps,
    ribbonModeLabel: string
  ): JSX.Element => {
    const reducedAnimations = isReducedAnimations() || this.props.disableAnimations;

    const { onRibbonModeToggleHoverFocus } = ribbonModeToggleProps;
    const onFocusHandler = () => {
      this.onModeChevronFocus();
      onRibbonModeToggleHoverFocus && onRibbonModeToggleHoverFocus();
    };

    return (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          const classNames = getRibbonBottomBarClassNames(theme, {
            ribbonMode: mode,
            reduceAnimations: reducedAnimations,
          });
          return (
            <div
              className={classNames.chevronContainer}
              onMouseEnter={onRibbonModeToggleHoverFocus}
              onFocus={onFocusHandler}
              onTouchStart={onRibbonModeToggleHoverFocus}
            >
              {this.buildRibbonModeChevronButton(ribbonModeToggleProps, mode, !!reducedAnimations, ribbonModeLabel)}
            </div>
          );
        }}
      </AppThemeConsumer>
    );
  };

  private buildRibbonModeChevronButton = (
    ribbonModeToggleProps: RibbonModeToggleProps,
    mode: RibbonMode,
    reducedAnimations: boolean,
    ribbonModeLabel: string
  ): JSX.Element => {
    const { appStrings, controlInMenuRendererFunction, preventMenuExecution } = this.props;
    const {
      onRibbonModeToggle,
      ribbonModeButtonProps,
      isRibbonModeToggleDisabled,
      ribbonModeToggleIcon,
    } = ribbonModeToggleProps;
    const { isExperimentalFeatureEnabled } = this.context;
    const shyRibbonIsEnabled = isShyRibbonEnabled(this.context);

    const modernUxExperimentOneIsEnabled = isExperimentalFeatureEnabled?.(
      ribbonExperiments.modernUxExperimentOneIsEnabled
    );
    const keytipProps = appStrings.ribbonModeChevronKeytip
      ? deriveTabKeytip(getTopLevelKeytipProps(appStrings.ribbonModeChevronKeytip), onRibbonModeToggle)
      : undefined;

    if (shyRibbonIsEnabled || modernUxExperimentOneIsEnabled) {
      return (
        <AppThemeConsumer>
          {(theme: ITheme) => {
            return (
              <RibbonFlyoutAnchor
                type="AppFlyoutAnchorProps"
                id={ribbonModeToggleId}
                label={appStrings.ribbonModeChevronLabel}
                variant="ribbonModeButton"
                styles={getModeChevronStyles(mode, theme, reducedAnimations, shyRibbonIsEnabled)}
                keytipProps={keytipProps}
                controlInMenuRendererFunction={controlInMenuRendererFunction}
                preventMenuExecution={preventMenuExecution}
                {...ribbonModeButtonProps}
              />
            );
          }}
        </AppThemeConsumer>
      );
    }
    return (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          return (
            <RibbonButton
              type="AppButtonProps"
              label={ribbonModeLabel}
              id={ribbonModeToggleId}
              onExecute={onRibbonModeToggle}
              icon={ribbonModeToggleIcon}
              hideLabel={true}
              variant="ribbonModeButton"
              variantId={`ribbonModeButton${mode}`}
              styles={getModeChevronStyles(mode, theme, reducedAnimations, shyRibbonIsEnabled)}
              disabled={isRibbonModeToggleDisabled}
              keytipProps={keytipProps}
              ariaExpanded={mode === 'Multiline'}
            />
          );
        }}
      </AppThemeConsumer>
    );
  };

  private onKeyDown = (ev: React.KeyboardEvent<HTMLElement>) => {
    let shouldMoveFocus = false;

    // If we got CTRL+Left/RightArrow, prevent default since we
    // are handling it here
    if (ev && (ev.which === KeyCodes.right || ev.which === KeyCodes.left) && ev.ctrlKey) {
      ev.preventDefault();
      shouldMoveFocus = true;
    }

    const validRibbonControlGroups = this.ribbonControlGroups.filter(group => group !== null && group.hasContainer());

    // If we did not get CTRL + Left/RightArrow OR we have less than
    // two valid chunks to navigate, we do not need to navigate.
    //
    // After this if check, we know that we are handling a CTRL + left/RightArrow AND
    // we have at least two valid ribbon chunks to attempt to navigate to
    if (!shouldMoveFocus || validRibbonControlGroups.length < 2) {
      return;
    }

    const ribbonControlGroupToFocus = (getRTL() ? ev.which === KeyCodes.left : ev.which === KeyCodes.right)
      ? this.getGroupWithOffset(validRibbonControlGroups, this.getNextIndexInGroups)
      : this.getGroupWithOffset(validRibbonControlGroups, this.getPreviousIndexInGroups);

    if (ribbonControlGroupToFocus) {
      ribbonControlGroupToFocus.focus();
    }
  };

  /**
   * For the given index find the "next" group.
   * Note, in this case "next" includes wrapping to the first group if we are at the last group
   * @param {number} startingIndex The index to find the next index of
   * @param {number} groupsLength The total length of the list the groups
   * @returns {number} the index of next group including wrapping
   */
  private getNextIndexInGroups(startingIndex: number, groupsLength: number): number {
    return (startingIndex + 1) % groupsLength;
  }

  /**
   * For the given index find the "previous" group.
   * Note, in this case "previous" includes wrapping to the last group if we are at the first group
   * @param {number} startingIndex The index to find the pervious index of
   * @param {number} groupsLength The total length of the list the groups
   * @returns {number} the index of next group including wrapping
   */
  private getPreviousIndexInGroups(startingIndex: number, groupsLength: number): number {
    return startingIndex > 0 ? startingIndex - 1 : groupsLength - 1;
  }

  /**
   * Attempt to find the previous group for the given element.
   * @param {RibbonControlGroups} ribbonControlGroups the valid groups to look through
   * @param {(currentlyFocusedGroupIndex: number, groupsLength: number) => number} calculateOffSet A function to calculate the offset from the focused index
   * @returns The group at the calculated offset, if found
   */
  private getGroupWithOffset(
    ribbonControlGroups: RibbonControlGroupBase[],
    calcualteOffset: (currentlyFocusedGroupIndex: number, groupsLength: number) => number
  ): RibbonControlGroupBase | undefined {
    for (let index = 0; index < ribbonControlGroups.length; index = index + 1) {
      if (ribbonControlGroups[index].containsFocus()) {
        // Now that we've found the group that currently contains focus,
        // return the control group that matches the calculated offset
        return ribbonControlGroups[calcualteOffset(index, ribbonControlGroups.length)];
      }
    }

    return undefined;
  }

  /**
   * Used to apply extra scaling steps when the spanning offset will overflow the container
   */
  private isScalingComplete = (scalingState: RibbonScalingState & { cacheKey: string }) => {
    // If we've previously calculated the spanning state for the current tab and the contents of the tab haven't changed, we can skip the computation
    if (
      this.spanningOffsetDict[this.props.id] &&
      this.spanningOffsetDict[this.props.id].controlGroups === this.props.controlGroups
    ) {
      // If the cacheKey's match, we can stop
      return this.spanningOffsetDict[this.props.id].cacheKey === scalingState.cacheKey;
    }

    const elementMap = getComponentsToMeasure();
    for (const elementId in elementMap) {
      let offset = getRectSpanningOffset(elementMap[elementId].getBoundingClientRect(), getRTL());
      if (offset >= 0) {
        offset += 8; // 8px is the extra buffer that we want on the left side of the control
        if (
          checkRibbonOffsetSpaceAvailable(
            offset,
            document.getElementById(ribbonBottomBarContainerId),
            document.getElementById(innerRibbonContainerId)
          )
        ) {
          this.spanningOffsetDict[this.props.id] = {
            componentId: elementId,
            spanningOffset: offset,
            cacheKey: scalingState.cacheKey,
            controlGroups: this.props.controlGroups,
          };
          return true;
        }
        return false;
      }
    }
    return true;
  };
}

function getRibbonOverflowMenuOpenAndCloseCommandHandler(
  controlGroups: RibbonControlGroupDefinitionSetSLR
): {
  onOverflowMenuOpen?: (parameter: MenuOpenOnExecuteParameter) => void;
  onOverflowMenuClose?: (id: string) => void;
} {
  const [controlGroupWithHandler] = controlGroups.groups.filter(controlGroup => !controlGroup.hasOwnOverflow);
  if (controlGroupWithHandler) {
    return {
      onOverflowMenuOpen: controlGroupWithHandler.onOverflowMenuOpen,
      onOverflowMenuClose: controlGroupWithHandler.onOverflowMenuClose,
    };
  }
  return {};
}
